import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  collection, query, where, getDocs, doc, getDoc,
  updateDoc, arrayUnion, arrayRemove, addDoc, orderBy
} from 'firebase/firestore';
import { db, auth } from './firebase';
import Navbar from './Navbar';
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { getInitials } from './utils';
import SwipeWrapper from './SwipeWrapper';
import Modal from './Modal';
import CollaboratorsModal from './CollaboratorsModal';
import AddToWishlistModal from './AddToWishlistModal';
import LazyImage from './LazyImage';
import LikesManager from './utils/likesManager';

export default function LikedWishlists() {
  const { t } = useTranslation();
  const [likedWishlists, setLikedWishlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLists, setExpandedLists] = useState({});
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [userWishlists, setUserWishlists] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [sortBy, setSortBy] = useState("recent");
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(null);
  const navigate = useNavigate();
  const [scrollPosition, setScrollPosition] = useState(0);

  // LikedWishlists page tracking removed to avoid conflicts

  useEffect(() => {
    const currentUserId = auth.currentUser?.uid;
    if (!currentUserId) return;

    const fetchLikedWishlists = async () => {
      try {
        // Get all public wishlists that the current user has liked
        const q = query(
          collection(db, "wishlists"),
          where("isPublic", "==", true),
          where("archived", "==", false),
          where("likes", "array-contains", currentUserId)
        );

        const snap = await getDocs(q);

        const wishlists = await Promise.all(
          snap.docs.map(async (docSnap) => {
            const data = docSnap.data();
            const safeData = {
              isPublic: data.isPublic ?? false,
              archived: data.archived ?? false,
              createdAt: data.createdAt ?? { seconds: Date.now() / 1000 },
              ...data
            };

            // Get user data for each wishlist
            const userSnap = await getDoc(doc(db, "users", safeData.userId));
            const userData = userSnap.exists() ? userSnap.data() : {};

            const baseWishlist = {
              id: docSnap.id,
              ...safeData,
              userName: userData.displayName || t('common.user'),
              avatarUrl: userData.avatarUrl || ""
            };

            // Use LikesManager to normalize the wishlist data
            const normalizedWishlist = LikesManager.normalizeWishlist(baseWishlist, currentUserId);
            
            // Since we're querying liked wishlists, ensure likedByMe is true
            return {
              ...normalizedWishlist,
              likedByMe: true
            };
          })
        );

        // Sort using LikesManager
        const sortedWishlists = sortBy === "popular"
          ? LikesManager.sortByPopularity(wishlists)
          : LikesManager.sortByDate(wishlists);

        setLikedWishlists(sortedWishlists);
      } catch (error) {
        console.error("Error fetching liked wishlists:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLikedWishlists();
  }, [currentUserId, sortBy]);

  const openModal = async (product, event) => {
    event?.preventDefault();
    event?.stopPropagation();

    setSelectedProduct(product);

    // Query per le wishlist di cui l'utente è proprietario
    const ownWishlistsQuery = query(
      collection(db, "wishlists"),
      where("userId", "==", currentUserId),
      where("archived", "==", false)
    );

    // Query per le wishlist dove l'utente è collaboratore
    const collaboratorWishlistsQuery = query(
      collection(db, "wishlists"),
      where("collaborators", "array-contains", currentUserId),
      where("archived", "==", false)
    );

    const [ownSnap, collaboratorSnap] = await Promise.all([
      getDocs(ownWishlistsQuery),
      getDocs(collaboratorWishlistsQuery)
    ]);

    const ownLists = ownSnap.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data(),
      isOwner: true 
    }));

    const collaboratorLists = collaboratorSnap.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data(),
      isOwner: false 
    }));

    const allLists = [...ownLists, ...collaboratorLists];
    setUserWishlists(allLists);
    setShowModal(true);
  };

  const addProductToWishlist = async (wishlistId) => {
    if (!selectedProduct) return;
    const ref = doc(db, "wishlists", wishlistId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    const updated = Array.isArray(data.products)
      ? [selectedProduct, ...data.products]
      : [selectedProduct];
    await updateDoc(ref, { products: updated });
    closeModal();
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

  const toggleLike = async (wishlistId) => {
    if (!currentUserId) return;

    const currentWishlist = likedWishlists.find(w => w.id === wishlistId);
    if (!currentWishlist) {
      console.log("❌ Wishlist non trovata:", wishlistId);
      return;
    }

    try {
      // Use the unified likes manager to handle the toggle
      const updatedWishlist = await LikesManager.toggleLike(
        wishlistId, 
        currentUserId, 
        currentWishlist
      );

      // If the user unliked the wishlist, remove it from the liked wishlists view
      if (!updatedWishlist.likedByMe) {
        setLikedWishlists(prev => prev.filter(w => w.id !== wishlistId));
      } else {
        // Update the local state with the result
        setLikedWishlists(prev => 
          LikesManager.updateWishlistsAfterLike(prev, wishlistId, updatedWishlist)
        );
      }

      // Send notification if liking (only when adding a like, not removing)
      if (updatedWishlist.likedByMe && updatedWishlist.userId !== currentUserId) {
        await addDoc(collection(db, "notifications"), {
          type: "like",
          createdAt: new Date(),
          isRead: false,
          recipientId: updatedWishlist.userId,
          senderId: currentUserId,
          wishlistId: wishlistId
        });
      }
    } catch (error) {
      console.error("❌ Errore toggle like in LikedWishlists:", error);
    }
  };

  const toggleExpand = (id) => {
    setExpandedLists(prev => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setCurrentUserId(user ? user.uid : null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('fromScroll');
    const fromPage = sessionStorage.getItem('fromPage');
    const fromBottomNav = location.state?.fromBottomNav;
    const shouldRestore = !fromBottomNav && savedScrollPosition && fromPage === '/liked';

    if (shouldRestore && likedWishlists.length > 0) {
      const timeoutId = setTimeout(() => {
        const position = parseInt(savedScrollPosition, 10);
        if (!isNaN(position)) {
          window.scrollTo({
            top: position,
            behavior: 'smooth'
          });

          sessionStorage.removeItem('fromScroll');
          sessionStorage.removeItem('fromPage');
        }
      }, 800);

      return () => clearTimeout(timeoutId);
    }
  }, [likedWishlists, location.state]);

  if (loading) {
    return (
      <div style={styles.page}>
        <Navbar />
        <div style={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <SwipeWrapper redirectTo="/feed">
      <div style={styles.page}>
        <Navbar />

        <div style={styles.headerNormal}>
          <div style={styles.header}>
            <h2 style={styles.title}>{t('notifications.wishlistLiked')}</h2>
            <p style={styles.subtitle}>{likedWishlists.length} {t('navigation.wishlists')}</p>
          </div>
          <div style={styles.filterRow}>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={styles.sort}
            >
              <option value="recent">{t('feed.sortRecent')}</option>
              <option value="popular">{t('feed.sortPopular')}</option>
            </select>
          </div>
        </div>

      <div style={styles.container}>
        {likedWishlists.length === 0 ? (
          <div style={styles.emptyState}>
            <p>{t('feed.noContent')}</p>
            <button 
              onClick={() => navigate('/feed')} 
              style={styles.feedButton}
            >
              {t('feed.explore')}
            </button>
          </div>
        ) : (
          likedWishlists.map(w => {
            const expanded = expandedLists[w.id];
            const visibleProducts = expanded ? w.products : (w.products || []).slice(0, 3);
            return (

              <div
                key={w.id}
                style={styles.card}
                onClick={() => {
                  sessionStorage.setItem('fromScroll', window.scrollY.toString());
                  sessionStorage.setItem('fromPage', '/liked');
                  navigate(`/wishlist/${w.id}`, { state: { from: "/liked" } });
                }}
              >
                <div style={styles.cardHeader}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
                     onClick={(e) => {
                           e.stopPropagation();
                           const currentScrollY = window.scrollY;
                           sessionStorage.setItem('fromScroll', currentScrollY.toString());
                           sessionStorage.setItem('fromPage', '/liked');
                           navigate(`/utente/${w.userId}`, { 
                             state: { 
                               from: "/liked"
                             } 
                           });
                         }}
                  >
                    {w.avatarUrl ? (
                      <img src={w.avatarUrl} alt="avatar" style={{ width: 28, height: 28, borderRadius: "50%" }} />
                    ) : (
                      <div style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: "#000",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: "bold"
                      }}>
                        {getInitials(w.userName)}
                      </div>
                    )}
                    <span 
                      onClick={(e) => {
                           e.stopPropagation();
                           const currentScrollY = window.scrollY;
                           sessionStorage.setItem('fromScroll', currentScrollY.toString());
                           sessionStorage.setItem('fromPage', '/liked');
                           navigate(`/utente/${w.userId}`, { 
                             state: { 
                               from: "/liked"
                             } 
                           });
                         }}
                      style={styles.username}
                    >
                      {w.userName}
                    </span>
                  </div>

                  <span
                    style={styles.likeIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleLike(w.id);
                    }}
                  >
                    <FaHeart style={{ color: "#000" }} />
                    {LikesManager.getLikesCount(w)}
                  </span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <h3 style={styles.wishlistTitle}>{w.name}</h3>
                  {w.isShared && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCollaboratorsModal(w.id);
                      }}
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "24px",
                        height: "24px",
                        border: "2px solid #000",
                        borderRadius: "50%",
                        backgroundColor: "transparent"
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                    </div>
                  )}
                </div>

                <div style={styles.tags}>
                  {(w.tags || []).map((tag, i) => (
                    <span key={i} style={styles.tag}>#{tag}</span>
                  ))}
                </div>

                <div style={styles.products}>
                  {visibleProducts.map((p, i) => (
                    <div key={i} style={styles.product}>
                      <LazyImage 
                        src={p.image} 
                        alt={p.title} 
                        style={styles.image} 
                      />
                      <a
                        href={p.url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={styles.link}
                      >
                        {p.title}
                      </a>
                      <button
                        style={styles.addBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          openModal(p, e);
                        }}
                      >
                        ＋
                      </button>
                    </div>
                  ))}
                  {w.products?.length > 3 && (
                    <button
                      style={styles.showMore}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpand(w.id);
                      }}
                    >
                      {expanded ? t('wishlist.showLess') : `+ ${w.products.length - 3} ${t('wishlist.moreProducts')}`}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <AddToWishlistModal
        showModal={showModal}
        onClose={closeModal}
        userWishlists={userWishlists}
        onAddToWishlist={addProductToWishlist}
      />

      {showCollaboratorsModal && (
        <CollaboratorsModal
          wishlistId={showCollaboratorsModal}
          wishlist={likedWishlists.find(w => w.id === showCollaboratorsModal)}
          currentUserId={auth.currentUser?.uid}
          onClose={() => setShowCollaboratorsModal(null)}          viewOnly={true}
        />
      )}
      </div>
    </SwipeWrapper>
  );
}

const styles = {
  page: {
    fontFamily: "Poppins, sans-serif",
    background: "#fff",
    color: "#000",
    minHeight: "100vh",    paddingBottom: "80px"
  },
  loading: {
    padding: "40px 20px",
    textAlign: "center",
    fontSize: "16px"
  },
  headerNormal: {
    background: "#fff",
    zIndex: 999,
    borderBottom: "1px solid #ddd"
  },
  header: {
    padding: "20px",
    paddingBottom: "10px"
  },
  filterRow: {
    padding: "0 20px 15px 20px",
    display: "flex",
    justifyContent: "flex-end"
  },
  sort: {
    fontSize: "13px",
    height: "36px",
    padding: "0 10px",
    borderRadius: "6px",
    border: "1px solid #aaa",
    background: "#fff",
    color: "#000",
    appearance: "none"
  },
  title: {
    fontSize: "24px",
    fontWeight: "600",
    margin: 0,
    marginBottom: "5px"
  },
  subtitle: {
    fontSize: "14px",
    color: "#666",
    margin: 0
  },
  container: {
    padding: "20px"
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#666"
  },
  feedButton: {
    marginTop: "20px",
    padding: "12px 24px",
    background: "#000",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    cursor: "pointer"
  },
  card: {
    background: "#fafafa",
    border: "1px solid #ddd",
    borderRadius: "10px",
    padding: "15px",
    marginBottom: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    cursor: "pointer"
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "10px"
  },
  username: {
    fontWeight: "600",
    cursor: "pointer",
    textDecoration: "underline"
  },
  likeIcon: {
    cursor: "pointer",
    fontSize: "18px",
    color: "#000",
    display: "flex",
    alignItems: "center",
    gap: "4px"
  },
  wishlistTitle: {
    marginBottom: "10px",
    fontSize: "16px",
    fontWeight: "600",
    margin: 0
  },
  tags: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "10px"
  },
  tag: {
    background: "#e0e0e0",
    borderRadius: "20px",
    padding: "4px 10px",
    fontSize: "12px"
  },
  products: {
    display: "flex",
    flexDirection: "column",
    gap: "10px"
  },
  product: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "8px"
  },
  image: {
    width: "60px",
    height: "60px",
    objectFit: "cover",
    borderRadius: "6px"
  },
  link: {
    textDecoration: "none",
    color: "#000",
    fontSize: "14px",
    fontWeight: "500",
    flex: 1
  },
  addBtn: {
    fontSize: "18px",
    padding: "4px 10px",
    background: "#000",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer"
  },
  showMore: {
    background: "none",
    border: "none",
    color: "#000",
    fontSize: "13px",
    cursor: "pointer",
    marginTop: "5px",
    textAlign: "left",
    padding: 0
  },
  modalContent: {
    background: "#fff",
    padding: "20px",
    borderRadius: "16px",
    width: "100%",
    maxWidth: "320px",
    boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
    textAlign: "center",
    maxHeight: "80vh",
    overflowY: "auto",
    position: "relative",
    margin: "auto"
  },

  modalBtn: {
    width: "100%",
    padding: "10px",
    marginBottom: "10px",
    background: "#000",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    cursor: "pointer"
  },
  modalCancel: {
    width: "100%",
    padding: "10px",
    background: "#e0e0e0",
    color: "#000",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    cursor: "pointer"
  },
  modalTitle: {
    marginBottom: "16px",
    fontSize: "18px",
    fontWeight: "600",
    color: "#000"
  }
};